"use client";

import {
  Button,
  type CommandSelectOption,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormCheckbox,
  FormCommandSelectField,
  FormInputField,
  useToast,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { zodResolver } from "@hookform/resolvers/zod";
import type { IHubCdv } from "@models";
import { useCreateHubCdv, useUpdateHubCdv } from "@query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface ICdvFormValues {
  id?: number;
  type: string;
  name: string;
  label: string;
  isDefault: boolean;
}

const DEFAULT_CDV_FORM: ICdvFormValues = {
  type: "",
  name: "",
  label: "",
  isDefault: false,
};

interface ICdvEditDialogProps {
  isLoadingCdvs: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: IHubCdv | null;
  typeSelectOptions: CommandSelectOption[];
}

export function CdvEditDialog({
  isLoadingCdvs,
  open,
  onOpenChange,
  editing,
  typeSelectOptions,
}: ICdvEditDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { mutateAsync: createCdv, isPending: isCreating } = useCreateHubCdv();
  const { mutateAsync: updateCdv, isPending: isUpdating } = useUpdateHubCdv();
  const isSaving = isCreating || isUpdating;

  const [errorTitle, setErrorTitle] = useState<string | null>(null);

  const cdvSchema = useMemo(
    () =>
      z.object({
        id: z.number().optional(),
        type: z
          .string()
          .trim()
          .min(
            1,
            t("hub.cdv_manager.type_required", {
              defaultValue: "Type is required",
            }),
          ),
        name: z
          .string()
          .trim()
          .min(
            1,
            t("hub.cdv_manager.name_required", {
              defaultValue: "Name is required",
            }),
          )
          .refine((val) => /^[^\s]+$/.test(val), {
            message: t("hub.cdv_manager.name_invalid", {
              defaultValue: "Name cannot contain spaces",
            }),
          }),
        label: z
          .string()
          .trim()
          .min(
            1,
            t("hub.cdv_manager.label_required", {
              defaultValue: "Label is required",
            }),
          ),
        isDefault: z.boolean(),
      }),
    [t],
  );

  const form = useForm<ICdvFormValues>({
    defaultValues: DEFAULT_CDV_FORM,
    mode: "onChange",
    resolver: zodResolver(cdvSchema),
  });
  const { control, handleSubmit, reset } = form;

  useEffect(() => {
    if (!open) {
      setErrorTitle(null);
      return;
    }
    if (editing) {
      reset({
        id: editing.id,
        type: editing.type,
        name: editing.name,
        label: editing.label,
        isDefault: editing.isDefault,
      });
    } else {
      reset(DEFAULT_CDV_FORM);
    }
  }, [open, editing, reset]);

  const onFormSubmit = handleSubmit(async (values) => {
    setErrorTitle(null);
    const typeValue = values.type.trim();
    try {
      if (values.id != null) {
        await updateCdv({
          id: values.id,
          type: typeValue,
          name: values.name.trim(),
          label: values.label.trim(),
          isDefault: values.isDefault,
        });
        toast.add({
          title: t("hub.cdv_manager.update_success", {
            defaultValue: "CDV updated",
          }),
          data: { type: "success" },
        });
      } else {
        await createCdv({
          type: typeValue,
          name: values.name.trim(),
          label: values.label.trim(),
          isDefault: values.isDefault,
        });
        toast.add({
          title: t("hub.cdv_manager.create_success", {
            defaultValue: "CDV created",
          }),
          data: { type: "success" },
        });
      }
      onOpenChange(false);
    } catch (error: any) {
      const apiMessage = error?.message || error?.response?.data?.message;

      const title =
        apiMessage ||
        t("hub.cdv_manager.save_error", {
          defaultValue: "Could not save CDV",
        });

      setErrorTitle(title);

      toast.add({
        title,
        data: { type: "error" },
      });
    }
  });

  const title = editing
    ? t("hub.cdv_manager.edit_title", { defaultValue: "Edit CDV" })
    : t("hub.cdv_manager.create_title", { defaultValue: "New CDV" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        showCloseButton
        onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {errorTitle && (
            <DialogDescription className="text-error-foreground">
              {errorTitle}
            </DialogDescription>
          )}
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onFormSubmit} className="flex flex-col">
            <DialogBody className="grid gap-4">
              <FormCommandSelectField
                isLoading={isLoadingCdvs}
                control={control}
                name="type"
                label={t("hub.cdv_manager.type", {
                  defaultValue: "Type",
                })}
                description={t("hub.cdv_manager.type_hint", {
                  defaultValue:
                    "Pick an existing type from the list or type a new type name.",
                })}
                options={typeSelectOptions}
                allowFreeText
                placeholder={t("hub.cdv_manager.type_placeholder", {
                  defaultValue: "Select or type…",
                })}
                required
              />
              <FormInputField
                control={control}
                isLoading={isLoadingCdvs}
                name="name"
                label={t("hub.cdv_manager.name", {
                  defaultValue: "Name (code)",
                })}
                placeholder={t("hub.cdv_manager.name_placeholder", {
                  defaultValue: "e.g. dxp_pro_tier",
                })}
                description={t("hub.cdv_manager.name_description", {
                  defaultValue:
                    "Stable identifier used in APIs and configuration. Cannot contain spaces.",
                })}
                required
                disabled={!!editing}
              />
              <FormInputField
                control={control}
                isLoading={isLoadingCdvs}
                name="label"
                label={t("hub.cdv_manager.label", {
                  defaultValue: "Label",
                })}
                placeholder={t("hub.cdv_manager.label_placeholder", {
                  defaultValue: "e.g. DXP Pro",
                })}
                description={t("hub.cdv_manager.label_description", {
                  defaultValue:
                    "Display name shown to users in catalog forms and details.",
                })}
                required
                maxLength={50}
              />
              <FormCheckbox
                control={control}
                isLoading={isLoadingCdvs}
                name="isDefault"
                label={t("hub.cdv_manager.is_default", {
                  defaultValue: "Default for this type",
                })}
                description={t("hub.cdv_manager.is_default_description", {
                  defaultValue:
                    "When enabled, this value can be pre-selected for new catalogs of this CDV type where applicable.",
                })}
              />
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}>
                {t("hub.administer.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button type="submit" disabled={isSaving}>
                {t("hub.cdv_manager.save", { defaultValue: "Save" })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
